import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLanguage } from '../contexts/LanguageContext';
import { DateField, TimeField } from './DateTimeField';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function EntryForm({ onSave, onUpdate, initialData, currentInvestment, percentage = 10, showFees = false }) {
  const { t, language } = useLanguage();
  const firstPurchase = initialData?.purchases?.[0];
  const [formData, setFormData] = useState({
    date: initialData?.date || '',
    time: firstPurchase?.time || initialData?.time || '',
    symbol: initialData?.symbol || initialData?.ticker || '',
    strike: initialData?.strike?.toString() || '',
    type: initialData?.type || 'Call',
    qty: (firstPurchase?.qty ?? initialData?.qty)?.toString() || '',
    buyPrice: (firstPurchase?.buyPrice ?? initialData?.buyPrice)?.toString() || '',
    limitPrice: initialData?.limitPrice?.toString() || '',
    sellDate: initialData?.sellDate || '',
    sellTime: initialData?.sellTime || '',
    sellPrice: initialData?.sellPrice?.toString() || '',
    manualProfit: (initialData?.manualProfit ?? null) !== null ? String(initialData.manualProfit) : '',
    brokerFee: initialData?.brokerFee ? String(initialData.brokerFee) : '',
    miscFee: initialData?.miscFee ? String(initialData.miscFee) : '',
    notes: initialData?.notes || ''
  });
  const [extraPurchases, setExtraPurchases] = useState(
    initialData?.purchases?.length > 1
      ? initialData.purchases.slice(1).map((p, idx) => ({
          key: `init-${idx}`,
          time: p.time || '',
          qty: p.qty?.toString() || '',
          price: p.buyPrice?.toString() || ''
        }))
      : []
  );

  const addExtraPurchase = () => setExtraPurchases(prev => [...prev, { key: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, time: '', qty: '', price: '' }]);
  const removeExtraPurchase = (i) => setExtraPurchases(prev => prev.filter((_, idx) => idx !== i));
  const updateExtraPurchase = (i, field, value) => {
    const finalValue = field === 'time' ? formatTimeInput(value) : value;
    setExtraPurchases(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: finalValue } : p));
  };

  const formatTimeInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    let formatted = '';
    if (digits.length > 0) {
      formatted += digits.slice(0, 2);
      if (digits.length > 2) {
        formatted += ':' + digits.slice(2, 4);
        if (digits.length > 4) {
          formatted += ':' + digits.slice(4, 6);
        }
      }
    }
    return formatted;
  };

  const formatDateInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let formatted = '';
    if (digits.length > 0) {
      formatted += digits.slice(0, 2);
      if (digits.length > 2) {
        formatted += '/' + digits.slice(2, 4);
        if (digits.length > 4) {
          formatted += '/' + digits.slice(4, 8);
        }
      }
    }
    return formatted;
  };

  const calculateTD = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    const parseTime = (t) => {
      const parts = t.split(':').map(Number);
      const h = parts[0] || 0;
      const m = parts[1] || 0;
      const s = parts[2] || 0;
      return h * 3600 + m * 60 + s;
    };
    const startSeconds = parseTime(startTime);
    const endSeconds = parseTime(endTime);
    const diffSeconds = endSeconds - startSeconds;
    const absSeconds = Math.abs(diffSeconds);
    const h = Math.floor(absSeconds / 3600);
    const m = Math.floor((absSeconds % 3600) / 60);
    const s = absSeconds % 60;
    const pad = (n) => n.toString().padStart(2, '0');
    const result = `${pad(h)}:${pad(m)}:${pad(s)}`;
    return diffSeconds < 0 ? `-${result}` : result;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let finalValue = value;
    if (['time', 'sellTime'].includes(name)) {
      finalValue = formatTimeInput(value);
    } else if (['date', 'sellDate'].includes(name)) {
      finalValue = formatDateInput(value);
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const buildPurchases = () => {
    const list = [
      { time: formData.time, qty: Number(formData.qty) || 0, buyPrice: Number(formData.buyPrice) || 0 },
      ...extraPurchases.map(p => ({ time: p.time, qty: Number(p.qty) || 0, buyPrice: Number(p.price) || 0 }))
    ];
    return list.filter(p => p.qty > 0 && p.buyPrice > 0);
  };

  const purchases = buildPurchases();
  const totalQty = purchases.reduce((s, p) => s + p.qty, 0);
  const totalInvestedCalc = purchases.reduce((s, p) => s + p.qty * p.buyPrice, 0) * 100;
  const avgBuyPrice = totalQty > 0 ? totalInvestedCalc / 100 / totalQty : 0;
  const targetSellPrice = avgBuyPrice * (1 + (percentage || 10) / 100);

  const handleSubmit = (e) => {
    e.preventDefault();
    const sellPrice = Number(formData.sellPrice) || 0;
    const totalInvested = totalInvestedCalc;
    const totalSale = totalQty * sellPrice * 100;
    const manualProfit = formData.manualProfit !== '' && !isNaN(Number(formData.manualProfit))
      ? Number(formData.manualProfit)
      : null;
    const brokerFee = Number(formData.brokerFee) || 0;
    const miscFee = Number(formData.miscFee) || 0;
    const totalFees = brokerFee + miscFee;
    const profit = (manualProfit !== null ? manualProfit : totalSale - totalInvested) - totalFees;
    const profitPercent = totalInvested !== 0 ? (profit / totalInvested) * 100 : 0;
    const finalTD = calculateTD(formData.time, formData.sellTime);

    const entry = {
      id: initialData?.id || generateUUID(),
      date: formData.date,
      time: formData.time,
      ticker: formData.symbol,
      symbol: formData.symbol,
      strike: Number(formData.strike) || 0,
      type: formData.type,
      qty: totalQty,
      buyPrice: totalQty > 0 ? Number(avgBuyPrice.toFixed(4)) : 0,
      purchases,
      totalInvested,
      limitPrice: Number(formData.limitPrice) || 0,
      sellDate: formData.sellDate,
      sellTime: formData.sellTime,
      sellPrice,
      manualProfit,
      brokerFee,
      miscFee,
      totalSale,
      profit,
      profitPercent,
      td: finalTD,
      notes: formData.notes,
      investment: initialData?.investment ?? (currentInvestment || 0) // Store the investment used for this trade
    };

    if (initialData && (!initialData.__aiDraft || initialData.__aiUpdate)) {
      onUpdate(entry);
    } else {
      onSave(entry);
    }
  };

  const sellPrice = Number(formData.sellPrice) || 0;
  const manualProfitPreview = formData.manualProfit !== '' && !isNaN(Number(formData.manualProfit))
    ? Number(formData.manualProfit)
    : null;
  const feesPreview = (Number(formData.brokerFee) || 0) + (Number(formData.miscFee) || 0);
  const grossProfitPreview = manualProfitPreview !== null
    ? manualProfitPreview
    : (totalQty * sellPrice * 100) - totalInvestedCalc;
  const currentProfit = grossProfitPreview - feesPreview;
  const isNegative = currentProfit < 0;

  const negativeNoteOptions = [
    t.form.emotionallyUnstable,
    t.form.incorrectLines,
    t.form.unstableBands,
    t.form.unstableCandles,
    t.form.earlyEntry,
    t.form.lateEntry,
    t.form.movedLimit,
    t.form.overtraded
  ];

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="entry-form">
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.date} (M/D/Y)</label>
        <DateField name="date" value={formData.date} onChange={handleInputChange} placeholder="MM/DD/YYYY" required testId="entry-date" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.time} (H:M:S)</label>
        <TimeField name="time" value={formData.time} onChange={handleInputChange} placeholder="HH:MM:SS" required testId="entry-time" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.symbol}</label>
        <input type="text" name="symbol" value={formData.symbol} onChange={handleInputChange} placeholder="AAPL" className="w-full p-2 border rounded-md text-sm" required data-testid="entry-symbol" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.strike}</label>
        <input type="number" name="strike" value={formData.strike} onChange={handleInputChange} placeholder="0.0" step="0.1" className="w-full p-2 border rounded-md text-sm" required />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.type}</label>
        <select name="type" value={formData.type} onChange={handleInputChange} className={`w-full p-2 border rounded-md text-sm font-bold ${formData.type === 'Call' ? 'text-green-600' : 'text-red-600'}`} data-testid="entry-type">
          <option value="Call" className="text-green-600">CALL</option>
          <option value="Put" className="text-red-600">PUT</option>
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.qty}</label>
        <input type="number" name="qty" value={formData.qty} onChange={handleInputChange} placeholder="0" className="w-full p-2 border rounded-md text-sm" required data-testid="entry-qty" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.buyPrice}</label>
        <input type="number" step="0.01" name="buyPrice" value={formData.buyPrice} onChange={handleInputChange} placeholder="0.00" className="w-full p-2 border rounded-md text-sm" required data-testid="entry-buy-price" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.limitPrice}</label>
        <input type="number" step="0.01" name="limitPrice" value={formData.limitPrice} onChange={handleInputChange} placeholder="0.00" className="w-full p-2 border rounded-md text-sm" />
      </div>

      {/* Extra purchases (averaging down) */}
      <div className="sm:col-span-2 md:col-span-3 space-y-2" data-testid="extra-purchases-section">
        {extraPurchases.map((p, i) => (
          <div key={p.key} className="bg-amber-50 border border-amber-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end" data-testid={`extra-purchase-${i}`}>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-amber-700 uppercase">
                {language === 'es' ? `Compra ${i + 2} · Hora` : `Buy ${i + 2} · Time`}
              </label>
              <TimeField
                name={`extra-time-${i}`}
                value={p.time}
                onChange={(e) => updateExtraPurchase(i, 'time', e.target.value)}
                placeholder="HH:MM:SS"
                testId={`extra-purchase-time-${i}`}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-amber-700 uppercase">{t.bitacora.qty}</label>
              <input
                type="number"
                value={p.qty}
                onChange={(e) => updateExtraPurchase(i, 'qty', e.target.value)}
                placeholder="0"
                className="w-full p-2 border border-amber-200 rounded-md text-sm bg-white"
                data-testid={`extra-purchase-qty-${i}`}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-amber-700 uppercase">{t.bitacora.buyPrice}</label>
              <input
                type="number"
                step="0.01"
                value={p.price}
                onChange={(e) => updateExtraPurchase(i, 'price', e.target.value)}
                placeholder="0.00"
                className="w-full p-2 border border-amber-200 rounded-md text-sm bg-white"
                data-testid={`extra-purchase-price-${i}`}
              />
            </div>
            <button
              type="button"
              onClick={() => removeExtraPurchase(i)}
              className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-600 font-bold rounded-md text-xs transition-colors"
              data-testid={`remove-extra-purchase-${i}`}
            >
              ✕ {language === 'es' ? 'Quitar' : 'Remove'}
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addExtraPurchase}
          className="w-full py-2.5 border-2 border-dashed border-amber-300 text-amber-700 font-bold rounded-lg text-xs sm:text-sm hover:bg-amber-50 transition-colors"
          data-testid="add-extra-purchase-btn"
        >
          + {language === 'es' ? 'Agregar Entrada' : 'Add Entry'}
        </button>

        {extraPurchases.length > 0 && totalQty > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 grid grid-cols-3 gap-2 text-center" data-testid="average-summary">
            <div>
              <p className="text-[10px] font-bold text-indigo-500 uppercase">{language === 'es' ? 'Cantidad Total' : 'Total Qty'}</p>
              <p className="font-black text-indigo-800 text-sm sm:text-base" data-testid="avg-total-qty">{totalQty}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-indigo-500 uppercase">{language === 'es' ? 'Precio Promedio' : 'Average Price'}</p>
              <p className="font-black text-indigo-800 text-sm sm:text-base" data-testid="avg-price">${avgBuyPrice.toFixed(3)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-emerald-600 uppercase">{language === 'es' ? `Venta objetivo (${percentage}%)` : `Target sell (${percentage}%)`}</p>
              <p className="font-black text-emerald-700 text-sm sm:text-base" data-testid="avg-target-sell">${targetSellPrice.toFixed(3)}</p>
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.sellDate}</label>
        <DateField name="sellDate" value={formData.sellDate} onChange={handleInputChange} placeholder="MM/DD/YYYY" testId="entry-sell-date" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.sellTime}</label>
        <TimeField name="sellTime" value={formData.sellTime} onChange={handleInputChange} placeholder="HH:MM:SS" testId="entry-sell-time" />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.sellPrice}</label>
        <input type="number" step="0.01" name="sellPrice" value={formData.sellPrice} onChange={handleInputChange} placeholder="0.00" className="w-full p-2 border rounded-md text-sm" data-testid="entry-sell-price" />
      </div>

      <div className="space-y-1">
        <label className="text-xs font-bold text-purple-600 uppercase">
          {language === 'es' ? 'Ganancia Total (opcional)' : 'Total Profit (optional)'}
        </label>
        <input
          type="number"
          step="0.01"
          name="manualProfit"
          value={formData.manualProfit}
          onChange={handleInputChange}
          placeholder={language === 'es' ? 'Solo si difiere del cálculo' : 'Only if it differs from the calc'}
          className="w-full p-2 border border-purple-200 rounded-md text-sm bg-purple-50/40"
          data-testid="entry-manual-profit"
        />
      </div>
      {showFees && (
        <>
          <div className="space-y-1">
            <label className="text-xs font-bold text-amber-600 uppercase">
              Broker fee
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="brokerFee"
              value={formData.brokerFee}
              onChange={handleInputChange}
              placeholder="0.00"
              className="w-full p-2 border border-amber-200 rounded-md text-sm bg-amber-50/40"
              data-testid="entry-broker-fee"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-amber-600 uppercase">
              Misc fee
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="miscFee"
              value={formData.miscFee}
              onChange={handleInputChange}
              placeholder="0.00"
              className="w-full p-2 border border-amber-200 rounded-md text-sm bg-amber-50/40"
              data-testid="entry-misc-fee"
            />
            <p className="text-[9px] text-gray-400">
              {language === 'es' ? 'Los fees se descuentan del profit' : 'Fees are deducted from profit'}
            </p>
          </div>
        </>
      )}
      {showFees && feesPreview > 0 && (totalQty > 0 || manualProfitPreview !== null) && (
        <div className="sm:col-span-2 md:col-span-3 bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-3 gap-2 text-center" data-testid="fees-breakdown">
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase">{language === 'es' ? 'Ganancia bruta' : 'Gross profit'}</p>
            <p className={`font-black text-sm sm:text-base ${grossProfitPreview >= 0 ? 'text-gray-800' : 'text-red-600'}`} data-testid="fees-gross">
              ${grossProfitPreview.toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-amber-600 uppercase">Fees</p>
            <p className="font-black text-amber-700 text-sm sm:text-base" data-testid="fees-total">−${feesPreview.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-emerald-600 uppercase">{language === 'es' ? 'Ganancia neta' : 'Net profit'}</p>
            <p className={`font-black text-sm sm:text-base ${currentProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="fees-net">
              ${currentProfit.toFixed(2)}
            </p>
          </div>
        </div>
      )}
      <div className="sm:col-span-2 md:col-span-3 space-y-1">
        <label className="text-xs font-bold text-gray-500 uppercase">{t.bitacora.notes}</label>
        {isNegative ? (
          <select 
            name="notes" 
            value={formData.notes} 
            onChange={handleInputChange} 
            className="w-full p-2 border rounded-md text-sm bg-red-50 border-red-200 text-red-900 font-medium"
            data-testid="entry-notes-select"
          >
            <option value="">-- {t.form.selectNegativeReason} --</option>
            {negativeNoteOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input 
            type="text" 
            name="notes" 
            value={formData.notes} 
            onChange={handleInputChange} 
            placeholder={t.bitacora.notes + "..."} 
            className="w-full p-2 border rounded-md text-sm"
            data-testid="entry-notes-input"
          />
        )}
      </div>
      <div className="sm:col-span-2 md:col-span-3 pt-4">
        <button type="submit" className={`w-full ${initialData && (!initialData.__aiDraft || initialData.__aiUpdate) ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white py-3 rounded-lg font-bold transition-colors shadow-lg text-sm sm:text-base`} data-testid="save-entry-btn">
          {initialData && (!initialData.__aiDraft || initialData.__aiUpdate) ? t.form.updateEntry : t.form.saveTrade}
        </button>
      </div>
    </form>
  );
}
